import './popupUpsert.scss';

import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form, Button, Popup, ScrollView } from "devextreme-react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { CustomRule, FormRef, GroupItem, Item } from "devextreme-react/form";
import { PersonaleRapportinoDTO, PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON } from "../../../../data";
import { usePersonaleCommessaDataSource } from "../../../../datasources/personaleCommessaDataSource";
import { useScreenSize } from "../../../../utils/media-query";
import { ClickEvent } from "devextreme/ui/button";

export type PopupPersonaleEditHandle = {
    show: (json: string, idCommessa: number, dataRapportino: Date, isUpdate: boolean) => void
};

type Props = {
    onChange: (json: string) => void
};

type State = {
    json: string,
    idCommessa: number,
    dataRapportino: Date,
    visible: boolean,
    isUpdate: boolean
};

const PopupPersonaleEdit: React.ForwardRefRenderFunction<PopupPersonaleEditHandle, Props> = (props, ref) => {
    const emptyData: PersonaleRapportinoDTO = {
        idPersonale: undefined,
        dataAMattino: null,
        dataAPomeriggio: null,
        dataDaMattino: null,
        dataDaPomeriggio: null,
        dataIntervento: undefined,
        oreTotali: 0,
        oreViaggio: 0,
        pernottamenti: 0,
        personale: null,
        viaggio: 0
    }

    const [state, setState] = useState<State>({ json: '', idCommessa: undefined, dataRapportino: undefined, visible: false, isUpdate: false });
    const [formData, setFormData] = useState<PersonaleRapportinoDTO>();

    const formRef = useRef<FormRef>(null);
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();

    const personaleDataSource = usePersonaleCommessaDataSource(state.idCommessa);

    function hideInfo() {
        setState({ json: '', idCommessa: undefined, dataRapportino: undefined, visible: false, isUpdate: false });
    };

    useImperativeHandle(ref, () => ({
        show(json, idCommessa, dataRapportino, isUpdate) {
            setState({ json: json, idCommessa: idCommessa, dataRapportino: dataRapportino, visible: true, isUpdate: isUpdate });
        }
    }));

    useEffect(() => {
        if (state.json != '') {
            var obj = JSON.parse(state.json);
            var data = PersonaleRapportinoDTOFromJSON(obj);
            if (!state.isUpdate)
                data.idPersonale = undefined;
            
            setFormData({ ...data });
        } else {
            setFormData({
                ...emptyData,
                dataDaMattino: null,
                dataAMattino: null,
                dataDaPomeriggio: null,
                dataAPomeriggio: null
            });
        }
    }, [state.json, state.idCommessa, state.dataRapportino, state.visible, state.isUpdate]);

    function setDateWithTime(date: Date) {
        if (!date) return null;

        const newDate = new Date(state.dataRapportino);
        newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
        return newDate;
    };

    const validateDaMattino = (e) => {
        const dataDa = e.value;

        if ((dataDa && !formData.dataAMattino) || (!dataDa && formData.dataAMattino))
            return false;

        if (dataDa && formData.dataAMattino)
            return setDateWithTime(dataDa) < setDateWithTime(formData.dataAMattino);

        return true;
    };

    const validateAMattino = (e) => {
        const dataA = e.value;

        if ((formData.dataDaMattino && !dataA) || (!formData.dataDaMattino && dataA))
            return false;

        if (formData.dataDaMattino && dataA)
            return setDateWithTime(formData.dataDaMattino) < setDateWithTime(dataA);

        return true;
    };

    const validateDaPomeriggio = (e) => {
        const dataDa = e.value;

        if ((dataDa && !formData.dataAPomeriggio) || (!dataDa && formData.dataAPomeriggio))
            return false;

        if (dataDa && formData.dataAPomeriggio)
            return setDateWithTime(dataDa) < setDateWithTime(formData.dataAPomeriggio);

        return true;
    };

    const validateAPomeriggio = (e) => {
        const dataA = e.value;

        if ((formData.dataDaPomeriggio && !dataA) || (!formData.dataDaPomeriggio && dataA))
            return false;

        if (formData.dataDaPomeriggio && dataA)
            return setDateWithTime(formData.dataDaPomeriggio) < setDateWithTime(dataA);

        return true;
    };

    const onFieldDataChanged = (e) => {
        if (e.dataField == "dataDaMattino" || e.dataField == "dataAMattino" || e.dataField == "dataDaPomeriggio" || e.dataField == "dataAPomeriggio")
            formRef.current?.instance().validate();
    };

    const upsert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            //calcola ore totali
            let oreTotali = 0;
            var dataDaMattino = setDateWithTime(formData.dataDaMattino);
            var dataAMattino = setDateWithTime(formData.dataAMattino);
            var dataDaPomeriggio = setDateWithTime(formData.dataDaPomeriggio);
            var dataAPomeriggio = setDateWithTime(formData.dataAPomeriggio);

            if (dataDaMattino && dataAMattino) {
                const diffMs = dataAMattino.getTime() - dataDaMattino.getTime();
                if (diffMs > 0) oreTotali += diffMs / (1000 * 60 * 60);
            }

            if (dataDaPomeriggio && dataAPomeriggio) {
                const diffMs = dataAPomeriggio.getTime() - dataDaPomeriggio.getTime();
                if (diffMs > 0) oreTotali += diffMs / (1000 * 60 * 60);
            }

            formData.oreTotali = (Math.round(oreTotali * 10) / 10);
            formData.dataDaMattino = dataDaMattino;
            formData.dataAMattino = dataAMattino;
            formData.dataDaPomeriggio = dataDaPomeriggio;
            formData.dataAPomeriggio = dataAPomeriggio;

            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(formData));
            props.onChange(json);
            hideInfo();
        }
    };

    return (
        <React.Fragment>
            <Popup
                visible={state.visible}
                dragEnabled={true}
                hideOnOutsideClick={true}
                showCloseButton={true}
                fullScreen={(isSmall || isXSmall ? true : false)}
                showTitle={true}
                title="Personale"
                container=".dx-viewport"
                onHidden={hideInfo}
            >
                <Position at="center" my="center" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={upsert}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                <ScrollView>
                    <Form
                        className="rapportino-personale-edit"
                        ref={formRef} formData={formData}
                        labelMode="floating" labelLocation="left"
                        colCount={(isLarge || isMedium) ? 2 : 1}
                        onFieldDataChanged={onFieldDataChanged}
                    >
                        {
                            state.isUpdate ?
                                (
                                    <Item colSpan={1} dataField="personale" editorType="dxTextBox" editorOptions={{ disabled: true }} />
                                ) :
                                (
                                    <Item colSpan={1} dataField="idPersonale" label={{ text: "Personale" }}
                                        editorType="dxSelectBox" isRequired={true}
                                        editorOptions={{
                                            disabled: false,
                                            dataSource: personaleDataSource,
                                            displayExpr: 'descrizione',
                                            valueExpr: 'idPersonale',
                                            searchEnabled: true,
                                            showClearButton: true,
                                            onValueChanged: (e) => {
                                                formData.personale = e.value != null ? e.component.option('text') : "";
                                            }
                                        }}
                                    />
                                )
                        }

                        <Item colSpan={1} itemType="empty" visible={(isLarge || isMedium)} />

                        <GroupItem caption="Ore Mattino / Giorno" colSpan={1} colCount={2}>
                            <Item colSpan={1} dataField="dataDaMattino" label={{ text: "Dalle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateDaMattino}
                                    message="Orario non consentito"
                                />
                            </Item>

                            <Item colSpan={1} dataField="dataAMattino" label={{ text: "Alle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateAMattino}
                                    message="Orario non consentito"
                                />
                            </Item>
                        </GroupItem>

                        <GroupItem caption="Ore Pomeriggio" colSpan={1} colCount={2}>
                            <Item colSpan={1} dataField="dataDaPomeriggio" label={{ text: "Dalle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateDaPomeriggio}
                                    message="Orario non consentito"
                                />
                            </Item>

                            <Item colSpan={1} dataField="dataAPomeriggio" label={{ text: "Alle" }}
                                editorType="dxDateBox"
                                editorOptions={{
                                    displayFormat: "HH:mm",
                                    type: "time"
                                }}
                            >
                                <CustomRule
                                    reevaluate={true}
                                    validationCallback={validateAPomeriggio}
                                    message="Orario non consentito"
                                />
                            </Item>
                        </GroupItem>

                        <GroupItem caption="Trasferimenti" colSpan={(isLarge || isMedium) ? 2 : 1} colCount={3}>
                            <Item isRequired={true} dataField="oreViaggio" editorType="dxNumberBox"
                                editorOptions={{
                                    default: 0,
                                    min: 0
                                }}
                            />

                            <Item isRequired={true} dataField="viaggio" label={{ text: "Viaggi A/R (KM)" }}
                                editorType="dxNumberBox"
                                editorOptions={{
                                    default: 0,
                                    min: 0
                                }}
                            />

                            <Item isRequired={true} dataField="pernottamenti" editorType="dxNumberBox"
                                editorOptions={{
                                    default: 0,
                                    min: 0
                                }}
                            />
                        </GroupItem>
                    </Form>
                </ScrollView>
            </Popup>
        </React.Fragment>
    )
};

export default React.forwardRef(PopupPersonaleEdit);